import {
  APPROVAL_DOC_TYPES,
  APPROVAL_LIST_SCOPE_LABELS,
  APPROVAL_REQUEST_STATUS_LABELS,
  type ApprovalListScope,
  approvalDocTypeSchema,
  approvalListScopeSchema,
  approvalRequestStatusSchema,
  type ApprovalRequestSummary,
} from "@station/contracts";
import {
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Select,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ClipboardCheck, ListChecks, UserCheck } from "lucide-react";
import { type ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useApprovalRequests } from "../../../features/approvals/queries.js";
import {
  ApprovalsPage,
  ApprovalStatusBadge,
  DueBadge,
  docTypeLabel,
  useApprovalPermissions,
} from "../../../features/approvals/shared.js";
import { Amount } from "../../../features/accounting/shared.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDateTime } from "../../../lib/format.js";

const searchSchema = z.object({
  scope: approvalListScopeSchema.optional().catch(undefined),
  status: approvalRequestStatusSchema.optional().catch(undefined),
  docType: approvalDocTypeSchema.optional().catch(undefined),
});
type ApprovalSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_app/onaylar/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ApprovalListPage,
});

function ApprovalListPage(): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const permissions = useApprovalPermissions();
  // "Tüm talepler" ayrı yetki ister; yetkisi olmayan kullanıcıda sekme hiç çizilmez.
  const scopes: ApprovalListScope[] = permissions.canViewAll
    ? ["MINE_TO_DECIDE", "MY_REQUESTS", "ALL"]
    : ["MINE_TO_DECIDE", "MY_REQUESTS"];
  const scope = search.scope && scopes.includes(search.scope) ? search.scope : "MINE_TO_DECIDE";

  return (
    <ApprovalsPage
      title={t("approvals.list.title")}
      description={t("approvals.list.description")}
      actions={
        <>
          <Button variant="outline" asChild>
            <Link to="/onaylar/vekaletler">
              <UserCheck aria-hidden="true" />
              {t("approvals.delegations.title")}
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/onaylar/politikalar">
              <ListChecks aria-hidden="true" />
              {t("approvals.policies.title")}
            </Link>
          </Button>
        </>
      }
    >
      <Tabs
        value={scope}
        onValueChange={(next) =>
          void navigate({
            search: (previous) => ({ ...previous, scope: next as ApprovalListScope }),
            replace: true,
          })
        }
      >
        <TabsList aria-label={t("approvals.list.tabs")}>
          {scopes.map((item) => (
            <TabsTrigger key={item} value={item}>
              {APPROVAL_LIST_SCOPE_LABELS[item]}
            </TabsTrigger>
          ))}
        </TabsList>
        {scopes.map((item) => (
          <TabsContent key={item} value={item}>
            {scope === item ? <RequestList key={item} scope={item} search={search} /> : null}
          </TabsContent>
        ))}
      </Tabs>
    </ApprovalsPage>
  );
}

function RequestList({ scope, search }: { scope: ApprovalListScope; search: ApprovalSearch }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const filters = {
    scope,
    ...(search.status ? { status: search.status } : {}),
    ...(search.docType ? { docType: search.docType } : {}),
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useApprovalRequests(filters, pages.cursor);
  const setFilters = (patch: Partial<ApprovalSearch>) =>
    void navigate({
      search: (previous) =>
        Object.fromEntries(Object.entries({ ...previous, ...patch }).filter(([, value]) => value)),
      replace: true,
    });

  const columns = useMemo<DataTableColumn<ApprovalRequestSummary>[]>(
    () => [
      {
        id: "docType",
        header: t("approvals.fields.docType"),
        hideable: false,
        cell: (row) => docTypeLabel(row.docType),
        exportValue: (row) => docTypeLabel(row.docType),
      },
      {
        id: "title",
        header: t("approvals.fields.title"),
        hideable: false,
        cell: (row) => (
          <Link
            to="/onaylar/$id"
            params={{ id: row.id }}
            className="font-medium underline underline-offset-4"
          >
            {row.docNo ? `${row.docNo} · ` : ""}
            {row.title}
          </Link>
        ),
        exportValue: (row) => `${row.docNo ?? ""} ${row.title}`.trim(),
      },
      {
        id: "amount",
        header: t("approvals.fields.amount"),
        align: "right",
        cell: (row) => <Amount value={row.amount} />,
        exportValue: (row) => row.amount,
      },
      {
        id: "company",
        header: t("approvals.fields.company"),
        cell: (row) => [row.companyName, row.stationName].filter(Boolean).join(" · ") || "—",
        exportValue: (row) => [row.companyName, row.stationName].filter(Boolean).join(" · "),
      },
      {
        id: "requestedBy",
        header: t("approvals.fields.requestedBy"),
        cell: (row) => row.requestedByName ?? "—",
        exportValue: (row) => row.requestedByName,
      },
      {
        id: "requestedAt",
        header: t("approvals.fields.requestedAt"),
        cell: (row) => formatDateTime(row.requestedAt),
        exportValue: (row) => row.requestedAt,
      },
      {
        id: "currentStep",
        header: t("approvals.fields.currentStep"),
        align: "center",
        cell: (row) => (row.currentStep === null ? "—" : String(row.currentStep)),
        exportValue: (row) => row.currentStep,
      },
      {
        id: "dueAt",
        header: t("approvals.fields.dueAt"),
        cell: (row) => (row.dueAt ? <DueBadge dueAt={row.dueAt} /> : "—"),
        exportValue: (row) => row.dueAt,
      },
      {
        id: "status",
        header: t("common.status"),
        hideable: false,
        cell: (row) => <ApprovalStatusBadge status={row.status} />,
        exportValue: (row) => APPROVAL_REQUEST_STATUS_LABELS[row.status],
      },
    ],
    [t],
  );

  return (
    <DataTable
      caption={t("approvals.list.caption", { scope: APPROVAL_LIST_SCOPE_LABELS[scope] })}
      columns={columns}
      rows={list.data?.items ?? []}
      rowKey={(row) => row.id}
      loading={list.isPending}
      error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
      emptyState={
        <EmptyState
          headingLevel={2}
          icon={<ClipboardCheck />}
          title={t("approvals.list.emptyTitle")}
          description={t("approvals.list.emptyDescription")}
        />
      }
      filters={
        <>
          <FormField label={t("common.status")} className="w-44">
            {(control) => (
              <Select
                {...control}
                value={search.status ?? ""}
                onChange={(event) =>
                  setFilters({
                    status:
                      event.target.value === ""
                        ? undefined
                        : approvalRequestStatusSchema.parse(event.target.value),
                  })
                }
              >
                <option value="">{t("common.all")}</option>
                {approvalRequestStatusSchema.options.map((status) => (
                  <option key={status} value={status}>
                    {APPROVAL_REQUEST_STATUS_LABELS[status]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("approvals.fields.docType")} className="w-48">
            {(control) => (
              <Select
                {...control}
                value={search.docType ?? ""}
                onChange={(event) =>
                  setFilters({
                    docType:
                      event.target.value === "" ? undefined : approvalDocTypeSchema.parse(event.target.value),
                  })
                }
              >
                <option value="">{t("common.all")}</option>
                {APPROVAL_DOC_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {docTypeLabel(type)}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        </>
      }
      pagination={{
        hasPrevious: pages.hasPrevious,
        hasNext: list.data?.page.hasMore ?? false,
        onPrevious: pages.previous,
        onNext: () => pages.next(list.data?.page.nextCursor),
        pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
      }}
    />
  );
}
